import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { GraphDotnetService, graphql, StoreLocalizedLabel, UpdateStoreLocalizedLabelsInput } from 'app/shared/graph';

@Injectable({
    providedIn: 'root',
})
export class StoreLocalesService {
    private readonly graphDotnetService = inject(GraphDotnetService);

    listStoreSupportedLocales(storeId: string): Observable<string[]> {
        return this.graphDotnetService.query(graphql`
                query supportedLocales($storeId: String!) {
                    store(id: $storeId) {
                        supportedLocales
                    }
                }
            `,
            { storeId },
        ).pipe(map(x => x.store.supportedLocales));
    }

    getStoreLocalizedLabels(storeId: string, keys: string[]): Observable<Array<StoreLocalizedLabel>> {
        return this.graphDotnetService.query(graphql`
                query storeLocalizedLabels($storeId: String!, $keys: [String!]!) {
                    store(id: $storeId) {
                        localizedLabels(keys: $keys) {
                            key
                            labels {
                                culture
                                value
                            }
                        }
                    }
                }
            `,
            { storeId, keys },
        ).pipe(map(x => x.store.localizedLabels));
    }

    getStoreLocalizedLabelsAsRecord(storeId: string, keys: string[]): Observable<Record<string, Record<string, string>>> {
        return this.getStoreLocalizedLabels(storeId, keys)
            .pipe(
                map(labelTranslations => {
                    let translations: Record<string, Record<string, string>> = {};
                    for (let labelTranslation of labelTranslations) {
                        let keyTranslations: Record<string, string> = {};
                        for (let label of labelTranslation.labels) {
                            keyTranslations[label.culture] = label.value;
                        }
                        translations[labelTranslation.key] = keyTranslations;
                    }
                    return translations;
                }),
            );
    }


    getStoreLocalizedLabel(storeId: string, key: string): Observable<Record<string, string>> {
        return this.getStoreLocalizedLabelsAsRecord(storeId, [key])
            .pipe(map(translations => translations[key] ?? {}));
    }

    updateStoreLocalizedLabels(input: UpdateStoreLocalizedLabelsInput) {
        return this.graphDotnetService.mutate(graphql`mutation updateStoreLocalizedLabels($input: UpdateStoreLocalizedLabelsInput!){
                updateStoreLocalizedLabels(input: $input)
            }`, { input },
        ).pipe(map(x => x.updateStoreLocalizedLabels));
    }

    updateStoreLocalizedLabel(storeId: string, labelKey: string, translations: Record<string, string>) {
        return this.updateStoreLocalizedLabels({
            storeId,
            items: [
                {
                    key: labelKey,
                    labels: Object.entries(translations).map(([culture, value]) => ({ culture, value })),
                },
            ],
        });
    }
}
